import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ScoreBreakdown } from '@/components/ScoreBreakdown/ScoreBreakdown';
import type { ScoreBreakdownData } from '@/components/ScoreBreakdown/ScoreBreakdown';
import { userAppContext } from '@/context/Context';
import {
  fetchGdaEvidencesWithTimeline,
  getRowAssociationTimelineParams,
} from '@/services/evidenceTimelineService';

import { ScoreHistoryPanel } from './ScoreHistoryPanel';
import type { ScoreHistoryPanelState } from '../utils/searchResultsHelpers';

type HistoricalScoreTabContentProps = {
  row: any;
  source: string;
  tab: string;
};

function emptyPanelState(): ScoreHistoryPanelState {
  return {
    selectedYear: null,
    selectedYearFrom: null,
    selectedYearTo: null,
    yearInput: '',
    yearFromInput: '',
    yearToInput: '',
    tab: undefined,
  };
}

export function HistoricalScoreTabContent({ row, source, tab }: HistoricalScoreTabContentProps) {
  const userCtx = userAppContext() as {
    authorization?: unknown;
    setAuthError?: (e: unknown) => void;
  };
  const authorization = userCtx?.authorization;
  const setAuthErr = userCtx?.setAuthError ?? (() => {});

  const [panel, setPanel] = useState<ScoreHistoryPanelState>(() => emptyPanelState());
  const [breakdown, setBreakdown] = useState<ScoreBreakdownData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noBreakdownInResponse, setNoBreakdownInResponse] = useState(false);

  const rowKey = useMemo(() => {
    const assoc = getRowAssociationTimelineParams(row, tab);
    return assoc
      ? `${assoc.apiTab}:${assoc.diseaseFilter}:${assoc.geneSymbol ?? assoc.variantName ?? ''}`
      : '';
  }, [row, tab]);

  useEffect(() => {
    setPanel(emptyPanelState());
    setBreakdown(null);
    setError(null);
    setLoading(false);
    setNoBreakdownInResponse(false);
  }, [rowKey]);

  const assoc = useMemo(() => getRowAssociationTimelineParams(row, tab), [row, tab]);

  const requestSeq = useRef(0);

  const runFetch = useCallback(
    async (minPmidYear: number | null, maxPmidYear: number | null) => {
      if (!assoc || !source) {
        setError('Could not resolve gene/variant and disease for this row.');
        setBreakdown(null);
        return;
      }

      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      setNoBreakdownInResponse(false);

      try {
        const result = await fetchGdaEvidencesWithTimeline({
          source,
          diseaseFilter: assoc.diseaseFilter,
          page_number: 0,
          page_size: 50,
          authorization,
          setAuthError: setAuthErr,
          tab: assoc.apiTab,
          geneSymbol: assoc.geneSymbol,
          variantName: assoc.variantName,
          minPmidYear,
          maxPmidYear,
        });

        if (seq !== requestSeq.current) return;

        const gs = result.globalScore ?? null;
        setBreakdown(gs);
        setNoBreakdownInResponse(!gs);
      } catch {
        if (seq !== requestSeq.current) return;
        setBreakdown(null);
        setNoBreakdownInResponse(false);
        setError('Could not load historical score.');
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [assoc, authorization, setAuthErr, source],
  );

  const handleSelectionCommit = useCallback(
    (detail: { year?: number | null; yearFrom?: number | null; yearTo?: number | null }) => {
      const y = detail.year;
      const yf = detail.yearFrom;
      const yt = detail.yearTo;

      const hasRange =
        typeof yf === 'number' &&
        Number.isFinite(yf) &&
        typeof yt === 'number' &&
        Number.isFinite(yt) &&
        yf <= yt;

      if (hasRange) {
        void runFetch(Math.trunc(yf!), Math.trunc(yt!));
        return;
      }

      if (typeof y === 'number' && Number.isFinite(y)) {
        const yi = Math.trunc(y);
        void runFetch(yi, yi);
        return;
      }

      void runFetch(null, null);
    },
    [runFetch],
  );

  const geneLabel =
    tab === 'VDA' ? String(row.variantStrID ?? row.strID ?? '') : String(row.geneSymbol ?? '');
  const diseaseLabel = String(row.diseaseName ?? '');
  const associationLabel = [geneLabel, diseaseLabel].filter(Boolean).join(' — ');

  if (!assoc) {
    return (
      <p className="text-sm text-gray-500 px-1 py-2">
        Historical score is only available for GDA rows with complete association data.
      </p>
    );
  }

  return (
    <div className="flex flex-col min-h-0 gap-3">
      <div className="flex min-h-0 flex-col gap-3 lg:flex-row lg:items-stretch lg:gap-4">
        <aside className="flex min-h-0 w-full shrink-0 flex-col rounded-xl border border-slate-200 bg-white p-3 lg:w-[min(100%,22rem)] lg:max-w-sm">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Year selection
          </p>
          <div className="min-h-0 flex-1 overflow-y-auto">
            <ScoreHistoryPanel
              panel={panel}
              onUpdate={setPanel}
              hideHeader
              onSelectionCommit={handleSelectionCommit}
            />
          </div>
        </aside>

        <div className="flex min-h-[220px] min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white lg:min-h-[260px]">
          <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-2">
            <h3 className="mb-0 text-xs font-semibold uppercase tracking-wide text-gray-800">
              Score for selection
            </h3>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {loading ? (
              <p className="mt-6 text-center text-sm text-gray-500">Loading…</p>
            ) : error ? (
              <p className="mt-6 text-center text-sm text-red-600">{error}</p>
            ) : breakdown ? (
              <ScoreBreakdown data={breakdown} />
            ) : noBreakdownInResponse ? (
              <p className="mt-6 text-center text-sm text-gray-400">No score breakdown available.</p>
            ) : (
              <p className="mt-6 text-center text-sm text-gray-400">
                Choose a year or range and apply to see the score breakdown.
              </p>
            )}
          </div>
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2">
            <small className="text-gray-600">
              More about the score breakdown{' '}
              <Link to="/docs?section=metrics" className="text-blue-700 underline">
                in the docs
              </Link>
              .
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}
